/* eslint-disable no-undef-init */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Map, {
	Layer,
	MapRef,
	Marker,
	NavigationControl,
	Source,
} from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spinner, Typography } from 'design_system/src';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import type { FeatureCollection } from 'geojson';

import {
	InterestPointType,
	InterestPointTypeValues,
} from 'models/repossession/enums/repossession.enums';
import Supercluster from 'supercluster';
import { momentLocal } from 'models/shared/helpers/dates';
import { ReactComponent as Search } from 'design_system/src/static/icons/search.svg';
import { ReactComponent as Checkbox } from 'design_system/src/static/icons/checkmark.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-sm.svg';
import { ReactComponent as GPS } from 'design_system/src/static/icons/gps.svg';
import { GeotileHistoryLocationDto } from 'models/_external/geotile/interfaces/geotile.interface';
import { getCity } from 'localStorage/cityStorage';
import { getUser } from 'localStorage/authStorage';

import { geotileMapContext } from './GeotileMapContext';

import './geotileMap.scss';
import {
	DeviceLocation,
	hotspotHeatLayerDefinition,
	hotspotLayerDefinition,
} from './GeotileMap.constants';
import SauronChat from './SauronChat';

export const GeotileMap = () => {
	const mapRef = useRef<MapRef>(null);

	const {
		loading,
		loadingHistory,
		gpsLocations,
		gpsHistory,
		selectedMarker,
		setSelectedMarker,
	} = useContext(geotileMapContext);

	const [query, setQuery] = useState('');
	const [cursor, setCursor] = useState('grab');

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});

	const [interestPointTypeFilter, setInterestPointTypeFilter] = useState<{
		[key: string]: boolean;
	}>({
		[InterestPointType.signalVehicle]: true,
		[InterestPointType.noSignalVehicle]: true,
	});

	const [selectedHotspot, setSelectedHotspot] =
		useState<GeotileHistoryLocationDto>();

	useEffect(() => {
		const cities = getCity();
		const user = getUser();
		if (user?.company?.city && cities?.length) {
			const city = cities.find((cit) => cit._id === user.company.city);
			if (city) {
				setViewState(city.location);
			}
		}
	}, []);

	const supercluster = useMemo(
		() =>
			new Supercluster({
				radius: 75,
				maxZoom: 20,
			}),
		[],
	);

	const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
		const map = mapRef.current?.getMap();
		if (map === undefined) return;
		const features = map.queryRenderedFeatures(event.point, {
			layers: ['interest_points_circles'],
		});
		if (!features[0]?.properties?.point) return;
		setSelectedHotspot(JSON.parse(features[0].properties.point));
	};

	const hotspotsLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: gpsHistory.map((point, i) => ({
				id: `${point.tile_uuid}_${i}`,
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.longitude, point.latitude],
				},
				properties: {
					weight: 6 / gpsHistory.length,
					point,
				},
			})),
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...hotspotHeatLayerDefinition} />
				<Layer {...hotspotLayerDefinition} />
			</Source>
		) : null;
	}, [gpsHistory]);

	const markers = useMemo(() => {
		if (!gpsLocations) return [];
		const upperQuery = query.toUpperCase();
		const initialPointsFiltered: DeviceLocation[] = Object.values(
			gpsLocations,
		).filter(
			(point) =>
				(point.externalVehicleData.internalId
					.toUpperCase()
					.includes(upperQuery) ||
					point.uuid.toUpperCase().includes(upperQuery) ||
					point.externalVehicleData.description
						.toUpperCase()
						.includes(upperQuery)) &&
				interestPointTypeFilter[point.locationType],
		);
		supercluster.load(
			initialPointsFiltered.map((point) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.location.longitude, point.location.latitude],
				},
				properties: point,
			})),
		);

		const SelectedIcon: any = selectedMarker
			? InterestPointTypeValues[selectedMarker.locationType].icon
			: null;

		const baseMarkers = selectedMarker
			? [
					<Marker
						longitude={selectedMarker.location.longitude}
						latitude={selectedMarker.location.latitude}
						onClick={() => {
							onClickHandler(selectedMarker);
						}}
					>
						<div
							className={`br_xxl main_marker cursor_pointer ${
								InterestPointTypeValues[selectedMarker.locationType].color
							} selected_marker`}
						>
							<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
								<SelectedIcon className="dim_lg icon" />
								<Typography weight="600" scale="xsmall">
									{selectedMarker.externalVehicleData.internalId}
								</Typography>
							</div>
							<div className="marker_details p_x_md flex_center_col">
								<Typography weight="600" scale="xxsmall">
									{momentLocal(selectedMarker.location.updatedAt).format(
										'DD/MM/YYYY',
									)}
								</Typography>
								<Typography weight="400" scale="xxsmall">
									{momentLocal(selectedMarker.location.updatedAt).format(
										'HH:mm',
									)}
								</Typography>
							</div>
							<span className="arrow" />
						</div>
					</Marker>,
			  ]
			: supercluster
					.getClusters([-180, -85, 180, 85], Math.round(viewState.zoom))
					.map((cluster) => {
						const [longitude, latitude] = cluster.geometry.coordinates;

						if (cluster.properties.cluster) {
							return (
								<Marker
									key={`cluster-${cluster.id}`}
									longitude={longitude}
									latitude={latitude}
									onClick={() => handleClusterClick(cluster)}
								>
									<div className="cluster_1">
										<div className="cluster_2">
											<div className="cluster_3 dim_xxxl">
												<Typography
													weight="600"
													scale="xsmall"
													className="cluster_count"
												>
													{cluster.properties.point_count}
												</Typography>
											</div>
										</div>
									</div>
								</Marker>
							);
						}
						const point = cluster.properties as DeviceLocation;
						const Icon = InterestPointTypeValues[point.locationType].icon;
						return (
							<Marker
								key={point._id}
								longitude={longitude}
								latitude={latitude}
								onClick={() => {
									onClickHandler(point);
								}}
							>
								<div
									className={`br_xxl main_marker cursor_pointer ${
										InterestPointTypeValues[point.locationType].color
									}`}
								>
									<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
										<Icon className="dim_lg icon" />
										<Typography weight="600" scale="xsmall">
											{point.externalVehicleData.internalId}
										</Typography>
									</div>
									<div className="marker_details p_x_md flex_center_col">
										<Typography weight="600" scale="xxsmall">
											{momentLocal(point.location.updatedAt).format(
												'DD/MM/YYYY',
											)}
										</Typography>
										<Typography weight="400" scale="xxsmall">
											{momentLocal(point.location.updatedAt).format('HH:mm')}
										</Typography>
									</div>
									<span className="arrow" />
								</div>
							</Marker>
						);
					});
		const selectedHotspotMarker =
			selectedHotspot && selectedMarker
				? [
						<Marker
							key="hotspot_data"
							longitude={selectedHotspot.longitude}
							latitude={selectedHotspot.latitude}
							onClick={() => {
								setSelectedHotspot(undefined);
							}}
						>
							<div className="br_xxl main_marker cursor_pointer primary selected_marker">
								<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
									<GPS className="dim_lg icon" />
									<Typography weight="600" scale="xsmall">
										{selectedMarker.externalVehicleData.internalId}
									</Typography>
								</div>
								<div className="marker_details p_x_md flex_center_col">
									<Typography weight="600" scale="xxsmall">
										{momentLocal(selectedHotspot.timestamp).format(
											'DD/MM/YYYY',
										)}
									</Typography>
									<Typography weight="400" scale="xxsmall">
										{momentLocal(selectedHotspot.timestamp).format('HH:mm')}
									</Typography>
								</div>
								<span className="arrow" />
							</div>
						</Marker>,
				  ]
				: [];

		return [...baseMarkers, selectedHotspotMarker];
	}, [
		supercluster,
		viewState.zoom,
		selectedMarker,
		selectedHotspot,
		gpsLocations,
		query,
		interestPointTypeFilter,
	]);

	const handleClusterClick = useCallback(
		(cluster) => {
			const clusterExpansionZoom = supercluster.getClusterExpansionZoom(
				cluster.id,
			);
			mapRef.current?.flyTo({
				center: cluster.geometry.coordinates,
				zoom: clusterExpansionZoom,
			});
		},
		[supercluster, viewState],
	);

	const onClickHandler = (point: DeviceLocation) => {
		if (selectedMarker?._id === point._id) {
			setSelectedMarker(undefined);
			return;
		}
		setSelectedMarker(point);
	};

	useEffect(() => {
		setSelectedHotspot(undefined);
		if (selectedMarker) {
			mapRef.current?.flyTo({
				center: [
					selectedMarker.location.longitude,
					selectedMarker.location.latitude,
				],
				zoom: 15,
			});
		}
	}, [selectedMarker]);

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="w_100_per bg_neutral_0 flex_header p_x_lg p_y_md">
				<Typography scale="heading2" weight="600" className="text_primary_300">
					Mapa GPS
				</Typography>
			</div>
			<div className="flex_grow_1 overflow_hidden pos_relative">
				<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
					{loading && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando mapa...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					{loadingHistory && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando puntos de interes...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					<div className="repossession_map_input_cont ">
						<input
							type="text"
							className="repossession_map_input shadow_hard"
							placeholder="Buscar Vehículo"
							value={query}
							onChange={(e) => setQuery(e.target.value.toUpperCase())}
						/>
						<Search className="repossession_map_search_icon dim_lg text_primary_300" />
					</div>
					<div className="devices_map_legend">
						<div className=" bg_neutral_0 br_md p_md m_b_md">
							{Object.entries(InterestPointTypeValues)
								.filter(([key]) =>
									[
										InterestPointType.signalVehicle,
										InterestPointType.noSignalVehicle,
									].includes(key as InterestPointType),
								)
								.map(([key, type]) => (
									<div
										key={key}
										className="display_flex flex_align_center flex_gap_xs"
									>
										<type.icon className={` text_${type.realColor} dim_xl`} />
										<Typography
											scale="small"
											weight="600"
											className="flex_grow_1"
										>
											{type.title}
										</Typography>
										<div
											className={`repossession_map_input_checkbox br_xs m_l_md ${
												interestPointTypeFilter[key] ? 'selected' : ''
											}`}
											onClick={() =>
												setInterestPointTypeFilter({
													...interestPointTypeFilter,
													[key]: !interestPointTypeFilter[key],
												})
											}
											onKeyDown={() =>
												setInterestPointTypeFilter({
													...interestPointTypeFilter,
													[key]: !interestPointTypeFilter[key],
												})
											}
											role="checkbox"
											aria-checked={interestPointTypeFilter[key]}
											tabIndex={0}
										>
											<Checkbox className="repossession_map_input_checkbox_icon center" />
										</div>
									</div>
								))}
						</div>
					</div>
					{selectedMarker && (
						<>
							<div className="devices_map_status m_b_md">
								<div className=" bg_neutral_0 br_md m_b_md shadow_hard overflow_hidden">
									<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid flex_center flex_justify_between">
										<Typography scale="large" weight="600">
											{selectedMarker.externalVehicleData.internalId}
										</Typography>
										<Close
											className="dim_xl border_transparent cursor_pointer"
											onClick={() => setSelectedMarker(undefined)}
										/>
									</div>
									<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid flex_center flex_gap_lg flex_justify_between">
										<div>
											<Typography
												scale="medium"
												weight="400"
												className="m_b_md"
											>
												{selectedMarker.externalVehicleData.description}
											</Typography>
											<Typography
												scale="medium"
												weight="600"
												className="m_b_md"
											>
												GPS:
												<Typography
													scale="medium"
													weight="400"
													component="span"
												>
													{` ${selectedMarker.uuid ?? 'Sin datos'}`}
												</Typography>
											</Typography>
											<Typography scale="xsmall" weight="600">
												Última actualización:
											</Typography>
											<Typography scale="xsmall" weight="400">
												{selectedMarker.location.updatedAt
													? momentLocal(
															selectedMarker.location.updatedAt,
													  ).format('DD/MM/YYYY HH:mm')
													: 'Sin datos'}
											</Typography>
										</div>
									</div>
								</div>
							</div>
							<div className="devices_map_chat">
								<SauronChat device={selectedMarker} />
							</div>
						</>
					)}
					<Map
						mapLib={mapboxgl}
						mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
						onMove={(evt) => setViewState(evt.viewState)}
						mapStyle="mapbox://styles/mapbox/streets-v9"
						reuseMaps
						ref={mapRef}
						interactiveLayerIds={['interest_points_circles']}
						onMouseEnter={() => {
							setCursor('pointer');
						}}
						onMouseLeave={() => setCursor('grab')}
						onClick={handleMapClick}
						cursor={cursor}
						{...viewState}
					>
						<NavigationControl position="bottom-left" />
						{hotspotsLayer}
						{markers}
					</Map>
				</div>
			</div>
		</div>
	);
};
